<template>
    <slot name="header_close"/>

    <div class="wrapper_dialog">
        <h3 class="title">
            {{ $t("tiktok.settings.webhooks_title") }}
            <div class="number-block">
                <img class="icon" :src="tiktokIcon"/>
                <span>{{ info?.username }}</span>
            </div>
        </h3>

        <form class="content" novalidate @submit.prevent="updateWebhooks">
            <div class="field">
                <p class="subtitle">{{ $t("dialog.webhooks.inbox_hook") }}</p>
                <input
                    v-model="form.incoming"
                    :class="{ input_error: !!errors.incoming }"
                    placeholder="http(s)://example.com"
                />
                <div class="input-errors" :class="{ 'is-shown': !!errors.incoming }">
                    {{ errors.incoming }}
                </div>
            </div>

            <div class="field">
                <p class="subtitle">{{ $t("dialog.webhooks.status_hook") }}</p>
                <input
                    v-model="form.status"
                    :class="{ input_error: !!errors.status }"
                    placeholder="http(s)://example.com"
                />
                <div class="input-errors" :class="{ 'is-shown': !!errors.status }">
                    {{ errors.status }}
                </div>
            </div>

            <div class="action_container">
                <ButtonLoading
                    type="submit"
                    :loading="button.loading"
                    :disabled="button.disabled || !isSubmittable"
                >
                    {{ isNew ? $t("dialog.webhooks.add_button") : $t("dialog.webhooks.button") }}
                </ButtonLoading>
            </div>
        </form>
    </div>
</template>



<script setup>
// core
import {ref, reactive, computed, onMounted, getCurrentInstance} from "vue";
import {useStore} from "vuex";
import {useI18n} from "vue-i18n";
import {validateWebhookUrl} from "@/helpers/dialogValidation";

// components
import ButtonLoading from "@/components/Recurring/ButtonLoading.vue";

const store = useStore();
const tiktokIcon = require("@/assets/icons/tiktok.svg").default;
const {proxy} = getCurrentInstance();
const {t: $t} = useI18n();

const button = ref({
    loading: false,
    disabled: false,
});

const form = reactive({
    incoming: "",
    status: "",
});

const initialForm = reactive({
    incoming: "",
    status: "",
});

const errors = computed(() => ({
    incoming: validateWebhookUrl(form.incoming, $t),
    status: validateWebhookUrl(form.status, $t),
}));

const isSubmittable = computed(() => {
    const filled = !!form.incoming?.trim() && !!form.status?.trim();
    const valid = !errors.value.incoming && !errors.value.status;
    const changed =
        form.incoming !== initialForm.incoming || form.status !== initialForm.status;

    return filled && valid && changed;
});

const dialogParams = computed(() => store.state.dialogParams);
const info = computed(() => dialogParams.value?.info);
const isNew = computed(() => dialogParams.value?.action === "add");

const title = computed(() =>
    $t("dialog.webhooks.update_title", {
        messenger: "TikTok",
        number: info.value?.username,
    })
);

const onToggleLoadingButton = () => {
    button.value.loading = !button.value.loading;
    button.value.disabled = button.value.loading;
};

const updateWebhooks = async () => {
    const isFormCorrect = isSubmittable.value;
    if (!isFormCorrect) return;

    const webhooks = [
        {type: "inbox", url: form.incoming},
        {type: "status", url: form.status},
    ];

    onToggleLoadingButton();

    try {
        await store.dispatch("tiktok/saveWebhooks", {
            userId: info.value.user_id,
            webhooks,
            isNew: isNew.value,
            moshaToast: proxy.$moshaToast,
        });

        if (!!dialogParams.value?.next) {
            store.state.dialogParams.info.webhooks = webhooks;
            store.commit("setDialog", dialogParams.value.next);
            store.commit("setNextDialog", "");
        } else {
            store.commit("closeDialog");
        }
    } catch (e) {
        // the toast is raised by the store action
    } finally {
        await store.dispatch("tiktok/loadAccounts", {
            visibility: info.value?.visibility,
        });
        onToggleLoadingButton();
    }
};

onMounted(() => {
    if (isNew.value) return;

    (info.value?.webhooks ?? []).forEach((el) => {
        el.type === "inbox" ? (form.incoming = el.url) : (form.status = el.url);
    });

    initialForm.incoming = form.incoming;
    initialForm.status = form.status;
});
</script>

<style lang="scss" scoped>
@use "@/styles/dialog-form.scss";
</style>
